import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import Box from '@mui/joy/Box';
import Tabs from '@mui/joy/Tabs';
import TabList from '@mui/joy/TabList';
import Tab, { tabClasses } from '@mui/joy/Tab';
import TabPanel from '@mui/joy/TabPanel';
import List from '@mui/joy/List';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import ListItem from '@mui/joy/ListItem';
import { useGetNotificationsQuery, useMarkAllNotificationsAsReadMutation } from '../../services/api.notifications';
import {
  NotificationCategories,
  NotificationCategory,
  NotificationWithoutContent
} from '../../interfaces/notification.interface';
import BellIcon from '../../assets/images/utility-icons/bell.svg?react';
import useComponentVisible from '../../hooks/useComponentVisible';
import { ALL_NOTIFICATIONS_CATEGORIES_KEY, CategoryColorMap } from '../../data/notifications.constants';
import hexToRGB from '../../utils/hexToRGB';
import useCanSeeBlockedUsers from '../../hooks/useCanSeeBlockedUsers';

const LIST_SIZE = 4;

export default function NotificationBell() {
  const { isComponentVisible, toggleVisibility, ref, hideComponent } = useComponentVisible();
  const { data } = useGetNotificationsQuery({ isRead: false, limit: 20 });
  const notifications = data || [];
  const notReadCount = notifications.filter((x) => !x.isRead).length;

  return (
    <Box ref={ref} display="flex" justifyContent="center" alignItems="center" position="relative">
      <IconButton onClick={toggleVisibility} variant="plain" sx={{ '& svg path': { fill: '#000' } }} size="md">
        <BellIcon style={{ width: 35, height: 35 }} />
        {notReadCount > 0 ? (
          <div
            style={{
              position: 'absolute',
              width: 15,
              height: 15,
              borderRadius: '50%',
              background: '#FF6B00',
              color: 'white',
              top: 0,
              right: 5,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center'
            }}
          >
            <span
              style={{
                fontWeight: 700,
                fontSize: 12,
                lineHeight: 12
              }}
            >
              {notReadCount}
            </span>
          </div>
        ) : null}
      </IconButton>
      {isComponentVisible ? <Dropdown notifications={notifications} hideComponent={hideComponent} /> : null}
    </Box>
  );
}

function groupNotificationsByCategory(notifications?: NotificationWithoutContent[]) {
  const initial = {
    [NotificationCategories.MaintenanceNotice]: [],
    [NotificationCategories.ReleaseNotes]: [],
    [NotificationCategories.GeneralInformation]: [],
    [NotificationCategories.BannedUser]: []
  };
  if (!notifications?.length) {
    return initial;
  }
  return notifications?.reduce<Record<NotificationCategory, NotificationWithoutContent[]>>((acc, item) => {
    acc[item.category].push(item);
    return acc;
  }, initial);
}

type DropdownProps = {
  notifications: NotificationWithoutContent[];
  hideComponent: () => void;
};

function Dropdown({ notifications, hideComponent }: DropdownProps) {
  const [markAllNotificationsAsRead, { isLoading }] = useMarkAllNotificationsAsReadMutation();
  const canSeeBlockedUsers = useCanSeeBlockedUsers();

  const notificationsByCategory = useMemo(() => groupNotificationsByCategory(notifications), [notifications]);

  const areAllRead = notifications.filter((x) => x.isRead).length > 0;
  async function handleReadAll() {
    if (areAllRead) {
      return;
    }
    await markAllNotificationsAsRead({});
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        top: '100%',
        right: 0,
        padding: '10px 0',
        borderBottomLeftRadius: 10,
        borderBottomRightRadius: 10,
        borderTop: 0,
        background: 'white',
        boxShadow: '0px 4px 10px 0px rgba(0, 0, 0, 0.08)'
      }}
    >
      <Box sx={{ padding: '5px 15px' }} display="flex" alignItems="center" justifyContent="space-between">
        <Typography level="h4">Notifications</Typography>
        <Button variant="plain" color="neutral" onClick={handleReadAll} disabled={areAllRead} loading={isLoading}>
          Mark all as read
        </Button>
      </Box>
      <Tabs
        sx={{
          ['&& .MuiTabPanel-root']: {
            padding: 0
          }
        }}
      >
        <TabList
          sx={{
            [`&& .${tabClasses.root}`]: {
              flex: 'initial',
              bgcolor: 'transparent',
              '&:hover': {
                bgcolor: 'transparent'
              },
              [`&.${tabClasses.selected}`]: {
                '& p': {
                  color: 'primary.plainColor'
                },
                '&::after': {
                  color: 'primary.plainColor',
                  height: 2,
                  borderTopLeftRadius: 3,
                  borderTopRightRadius: 3
                }
              }
            }
          }}
        >
          <Tab indicatorInset variant="plain">
            <Typography level="body-sm" noWrap>
              All
            </Typography>
          </Tab>
          <Tab indicatorInset variant="plain">
            <Typography level="body-sm" noWrap>
              Maintenance Notices
            </Typography>
          </Tab>
          <Tab indicatorInset variant="plain">
            <Typography level="body-sm" noWrap>
              Release Notes
            </Typography>
          </Tab>
          <Tab indicatorInset variant="plain">
            <Typography level="body-sm" noWrap>
              General Informations
            </Typography>
          </Tab>
          {canSeeBlockedUsers && (
            <Tab indicatorInset variant="plain">
              <Typography level="body-sm" noWrap>
                Blocked Users
              </Typography>
            </Tab>
          )}
        </TabList>
        <TabPanel value={0}>
          <NotifiactionsList hideComponent={hideComponent} notifications={notifications || []} />
        </TabPanel>
        <TabPanel value={1}>
          <NotifiactionsList
            notifications={notificationsByCategory[NotificationCategories.MaintenanceNotice]}
            hideComponent={hideComponent}
          />
        </TabPanel>
        <TabPanel value={2}>
          <NotifiactionsList
            notifications={notificationsByCategory[NotificationCategories.ReleaseNotes]}
            hideComponent={hideComponent}
          />
        </TabPanel>
        <TabPanel value={3}>
          <NotifiactionsList
            notifications={notificationsByCategory[NotificationCategories.GeneralInformation]}
            hideComponent={hideComponent}
          />
        </TabPanel>
        {canSeeBlockedUsers && (
          <TabPanel value={4}>
            <NotifiactionsList
              notifications={notificationsByCategory[NotificationCategories.BannedUser]}
              hideComponent={hideComponent}
            />
          </TabPanel>
        )}
      </Tabs>
    </Box>
  );
}

type NotifiactionsListProps = {
  category?: NotificationCategory;
  hideComponent: () => void;
  notifications: NotificationWithoutContent[];
};

function NotifiactionsList({ category, hideComponent, notifications }: NotifiactionsListProps) {
  return (
    <>
      <List>
        {notifications
          ?.slice(0, LIST_SIZE)
          .map((notification) => (
            <NotifiactionsListItem key={notification._id} notification={notification} hideComponent={hideComponent} />
          ))}
      </List>
      <Box display="flex" justifyContent="center">
        <Link
          to={`/notifications/${category || ALL_NOTIFICATIONS_CATEGORIES_KEY}`}
          style={{ textDecoration: 'none' }}
          onClick={hideComponent}
        >
          <Typography level="body-md">View all</Typography>
        </Link>
      </Box>
    </>
  );
}

type NotifiactionsListItemProps = {
  notification: NotificationWithoutContent;
  hideComponent: () => void;
};

function NotifiactionsListItem({ notification, hideComponent }: NotifiactionsListItemProps) {
  const title = notification.title;
  const color = CategoryColorMap[notification.category];
  const dateTimeText = moment(notification.createdAt).fromNow();

  function handleClick() {
    hideComponent();
  }

  return (
    <ListItem sx={{ padding: 0, margin: '3px 0' }} onClick={handleClick}>
      <Link to={`/notifications/view/${notification._id}`} style={{ width: '100%', textDecoration: 'none' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            borderLeft: `3px solid ${color}`,
            background: notification.isRead === false ? hexToRGB(color, 0.05) : '#fff',
            fontWeight: 700,
            padding: '20px'
          }}
        >
          <Typography level="body-sm">{title}</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography level="body-sm">{dateTimeText}</Typography>
            {notification.isRead === false ? (
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#FF6B00' }} />
            ) : null}
          </Box>
        </Box>
      </Link>
    </ListItem>
  );
}
